import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import dayjs from "dayjs";
import EditStatusModal from "./edit";
import { jwtDecode } from "jwt-decode";

function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const CampaignTable = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const [searchKey, setSearchKey] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);

  const [dateRange, setDateRange] = useState({
    label: "All",
    start: null,
    end: null,
  });

  const [statusFilter, setStatusFilter] = useState("All");

  // Sorting states for all columns
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "none",
  });

  // Edit Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [userRole, setUserRole] = useState("");
  const [username, setUsername] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role || "user");
        setUsername(decoded.username || decoded.email || "");
      } catch (err) {
        console.error("Token decode failed:", err);
        setError("Invalid authentication token.");
      }
    } else {
      setError("Authentication token not found.");
    }
  }, []);

  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (username && userRole) fetchCampaignData();
  }, [username, userRole]);

  useEffect(() => {
    let filtered = rows.filter((row) => {
      const status = row.cmp_status?.toLowerCase() || "";
      return ["completed", "cancelled", "failed"].includes(status);
    });

    if (statusFilter !== "All") {
      filtered = filtered.filter(
        (row) => row.cmp_status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (dateRange.label !== "All" && dateRange.start && dateRange.end) {
      filtered = filtered.filter((row) => {
        const campaignDate = dayjs(
          row.completed || row.dateTime || row.createdAt || null
        );
        if (!campaignDate.isValid()) return false;
        return (
          campaignDate.isAfter(dateRange.start.startOf("day")) &&
          campaignDate.isBefore(dateRange.end.endOf("day"))
        );
      });
    }

    setFilteredRows(filtered);
  }, [rows, statusFilter, dateRange]);

  // ✅ FIXED: Enhanced sorting logic with proper handling for all columns
  useEffect(() => {
    if (sortConfig.key && sortConfig.direction !== "none") {
      let sorted = [...filteredRows];

      sorted.sort((a, b) => {
        const key = sortConfig.key;
        const dir = sortConfig.direction === "asc" ? 1 : -1;

        // ✅ FIXED: Date sorting by month and year
        if (key === "date") {
          const dateA = parseDate(a.completed || a.dateTime || a.createdAt);
          const dateB = parseDate(b.completed || b.dateTime || b.createdAt);

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          // Sort by year first, then month, then day
          if (dateA.year !== dateB.year) {
            return dir * (dateA.year - dateB.year);
          }
          if (dateA.month !== dateB.month) {
            return dir * (dateA.month - dateB.month);
          }
          return dir * (dateA.day - dateB.day);
        }

        // ✅ FIXED: Time sorting
        if (key === "time") {
          const dateA = parseDate(a.completed || a.dateTime || a.createdAt);
          const dateB = parseDate(b.completed || b.dateTime || b.createdAt);

          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          return dir * (dateA.timestamp - dateB.timestamp);
        }

        // ✅ FIXED: Count sorting (numeric)
        if (key === "count") {
          const valA = parseInt(a.count || 0);
          const valB = parseInt(b.count || 0);
          return dir * (valA - valB);
        }

        // ✅ FIXED: Campaign ID sorting (alphanumeric)
        if (key === "id") {
          const valA = (a.id || a.campaign_id || "")
            .toString()
            .toLowerCase()
            .trim();
          const valB = (b.id || b.campaign_id || "")
            .toString()
            .toLowerCase()
            .trim();

          if (valA === "" && valB === "") return 0;
          if (valA === "") return 1;
          if (valB === "") return -1;

          return valA.localeCompare(valB) * dir;
        }

        // ✅ FIXED: User ID sorting
        if (key === "user_id") {
          const valA = (a.user_id || a.username || "")
            .toString()
            .toLowerCase()
            .trim();
          const valB = (b.user_id || b.username || "")
            .toString()
            .toLowerCase()
            .trim();

          if (valA === "" && valB === "") return 0;
          if (valA === "") return 1;
          if (valB === "") return -1;

          return valA.localeCompare(valB) * dir;
        }

        // ✅ FIXED: Creator name sorting
        if (key === "persion_name") {
          const valA = (a.persion_name || a.person_name || "")
            .toString()
            .toLowerCase()
            .trim();
          const valB = (b.persion_name || b.person_name || "")
            .toString()
            .toLowerCase()
            .trim();

          if (valA === "" && valB === "") return 0;
          if (valA === "") return 1;
          if (valB === "") return -1;

          return valA.localeCompare(valB) * dir;
        }

        // ✅ FIXED: Status sorting
        if (key === "cmp_status") {
          const valA = (a.cmp_status || "").toString().toLowerCase().trim();
          const valB = (b.cmp_status || "").toString().toLowerCase().trim();

          if (valA === "" && valB === "") return 0;
          if (valA === "") return 1;
          if (valB === "") return -1;

          return valA.localeCompare(valB) * dir;
        }

        // Default string comparison for other columns
        const valA = (a[key] || "").toString().toLowerCase().trim();
        const valB = (b[key] || "").toString().toLowerCase().trim();

        if (valA === "" && valB === "") return 0;
        if (valA === "") return 1;
        if (valB === "") return -1;

        return valA.localeCompare(valB) * dir;
      });

      setFilteredRows(sorted);
    }
  }, [sortConfig, filteredRows.length]);

  // ✅ NEW: Helper function to parse dates consistently
  const parseDate = (dateTimeString) => {
    if (!dateTimeString) return null;

    let dateObj = dayjs(dateTimeString);

    // If dayjs can't parse it automatically, try common formats
    if (!dateObj.isValid()) {
      dateObj = dayjs(dateTimeString, "YYYY-MM-DDTHH:mm:ss.SSSZ");
    }
    if (!dateObj.isValid()) {
      dateObj = dayjs(dateTimeString, "YYYY-MM-DD HH:mm:ss");
    }
    if (!dateObj.isValid()) {
      dateObj = dayjs(dateTimeString, "DD/MM/YYYY HH:mm:ss");
    }
    if (!dateObj.isValid()) {
      dateObj = dayjs(dateTimeString, "MM/DD/YYYY HH:mm:ss");
    }

    if (!dateObj.isValid()) return null;

    return {
      dateObj,
      timestamp: dateObj.valueOf(),
      year: dateObj.year(),
      month: dateObj.month() + 1, // month() returns 0-11, so +1 for 1-12
      day: dateObj.date(),
      hour: dateObj.hour(),
      minute: dateObj.minute(),
      second: dateObj.second(),
    };
  };

  const fetchCampaignData = async () => {
    setLoading(true);
    setError("");
    try {
      let response;
      if (userRole === "admin") {
        response = await axios.post(
          "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/report_all",
          { role: userRole, username }
        );
        setRows(response.data["body-json"]?.body?.Items || []);
      } else {
        response = await axios.post(
          "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/camp_report",
          { username }
        );
        setRows(response.data?.["body-json"]?.body?.Items || []);
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error("Error fetching campaign data:", err);
    } finally {
      setLoading(false);
    }
  };

  const callSearchApi = async (key) => {
    if (!key.trim()) {
      fetchCampaignData();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/report_search?key=${encodeURIComponent(
          key
        )}`
      );

      let items = [];
      if (response.data && response.data.body) {
        items =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)?.Items || []
            : response.data.body.Items || [];
      }
      setRows(items);
      setPage(0);
    } catch (err) {
      console.error("Search API error:", err);
      setRows([]);
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce(callSearchApi, 500), []);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    debouncedSearch(value);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleStatusFilterClick = (e) =>
    setStatusFilterAnchorEl(e.currentTarget);
  const handleStatusFilterClose = () => setStatusFilterAnchorEl(null);
  const handleStatusFilterSelect = (status) => {
    setStatusFilter(status);
    handleStatusFilterClose();
  };

  const handleDateMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleDateMenuClose = () => setAnchorEl(null);

  const setPresetRange = (preset) => {
    switch (preset) {
      case "All":
        setDateRange({ label: "All", start: null, end: null });
        break;
      case "Today":
        setDateRange({ label: "Today", start: dayjs(), end: dayjs() });
        break;
      case "Yesterday":
        setDateRange({
          label: "Yesterday",
          start: dayjs().subtract(1, "day"),
          end: dayjs().subtract(1, "day"),
        });
        break;
      case "Last 7 Days":
        setDateRange({
          label: "Last 7 Days",
          start: dayjs().subtract(6, "day"),
          end: dayjs(),
        });
        break;
      case "This Month":
        setDateRange({
          label: "This Month",
          start: dayjs().startOf("month"),
          end: dayjs().endOf("month"),
        });
        break;
      default:
        break;
    }
    handleDateMenuClose();
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        const nextDir =
          prev.direction === "none"
            ? "asc"
            : prev.direction === "asc"
            ? "desc"
            : "none";
        return { key, direction: nextDir };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    if (sortConfig.direction === "asc")
      return <ArrowUpwardIcon fontSize="small" />;
    if (sortConfig.direction === "desc")
      return <ArrowDownwardIcon fontSize="small" />;
    return null;
  };

  // ✅ FIXED: Enhanced date formatting with better parsing
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: "N/A", time: "N/A" };

    let dateObj = dayjs(dateTimeString);

    // If dayjs can't parse it automatically, try common formats
    if (!dateObj.isValid()) {
      // Try ISO format
      dateObj = dayjs(dateTimeString, "YYYY-MM-DDTHH:mm:ss.SSSZ");
      if (!dateObj.isValid()) {
        // Try another common format
        dateObj = dayjs(dateTimeString, "YYYY-MM-DD HH:mm:ss");
      }
    }

    if (!dateObj.isValid()) return { date: "N/A", time: "N/A" };

    return {
      date: dateObj.format("MM/DD/YYYY"),
      time: dateObj.format("hh:mm A"),
      timestamp: dateObj.valueOf(),
      month: dateObj.month(),
      year: dateObj.year(),
    };
  };

  // ✅ CHANGED: Download as CSV instead of Excel
  const downloadCSV = async (row) => {
    try {
      setSnackbar({
        open: true,
        message: "Downloading CSV data...",
        severity: "info",
      });

      let csvData = [];
      let headers = [];

      if (row.csv) {
        try {
          const response = await fetch(row.csv);
          const csvText = await response.text();
          const lines = csvText.split("\n");
          headers = lines[0].split(",").map((header) => header.trim());

          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === "") continue;
            const values = lines[i].split(",").map((value) => value.trim());
            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index] || "";
            });
            csvData.push(rowData);
          }
        } catch (error) {
          console.error("Error fetching CSV:", error);
        }
      }

      // If no CSV data from URL, create basic campaign data
      if (csvData.length === 0) {
        const { date, time } = formatDateTime(
          row.completed || row.dateTime || row.createdAt
        );
        headers = [
          "CampaignID",
          "Status",
          "UserID",
          "Creator",
          "Count",
          "Date",
          "Time",
          "Remarks",
        ];
        csvData = [
          {
            CampaignID: row.id || row.campaign_id || "N/A",
            Status: row.cmp_status || "N/A",
            UserID: row.user_id || row.username || "N/A",
            Creator: row.persion_name || row.person_name || "N/A",
            Count: row.count || "0",
            Date: date,
            Time: time,
            Remarks: row.remarks || "N/A",
          },
        ];
      }

      // Convert data to CSV format
      const csvHeaders = headers.join(",");
      const csvRows = csvData.map((item) =>
        headers
          .map((header) => {
            const value = item[header] || "";
            // Escape quotes and wrap in quotes if contains comma or quotes
            const escapedValue = String(value).replace(/"/g, '""');
            return escapedValue.includes(",") || escapedValue.includes('"')
              ? `"${escapedValue}"`
              : escapedValue;
          })
          .join(",")
      );

      const csvContent = [csvHeaders, ...csvRows].join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const campaignId = row.id || row.campaign_id || "campaign";
      const status = row.cmp_status?.toLowerCase() || "unknown";
      const fileName = `campaign_${status}_${campaignId}_${dayjs().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "CSV file downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setSnackbar({
        open: true,
        message: "Failed to download CSV file",
        severity: "error",
      });
    }
  };

  const cellStyle = { py: 0.5, px: 1.5, fontSize: "0.85rem" };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          borderRadius: "20px",
          mb: 3,
          gap: 2,
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          p: 2,
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search By Campaign ID, User ID"
          value={searchKey}
          onChange={handleSearchChange}
          sx={{
            ".MuiInputBase-root": {
              borderRadius: "30px",
              backgroundColor: "#f9f9f9",
              height: 35,
              width: { xs: "100%", sm: "800px" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            onClick={handleStatusFilterClick}
            startIcon={<FilterListIcon />}
            size="small"
          >
            Status: {statusFilter}
          </Button>
          <Menu
            anchorEl={statusFilterAnchorEl}
            open={Boolean(statusFilterAnchorEl)}
            onClose={handleStatusFilterClose}
          >
            {["All", "Completed", "Cancelled", "Failed"].map((option) => (
              <MenuItem
                key={option}
                onClick={() => handleStatusFilterSelect(option)}
              >
                {option}
              </MenuItem>
            ))}
          </Menu>

          <Button onClick={handleDateMenuClick} size="small">
            {dateRange.label === "All"
              ? "All Dates"
              : `${dateRange.start.format(
                  "MM/DD/YYYY"
                )} - ${dateRange.end.format("MM/DD/YYYY")}`}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleDateMenuClose}
          >
            {["All", "Today", "Yesterday", "Last 7 Days", "This Month"].map(
              (preset) => (
                <MenuItem key={preset} onClick={() => setPresetRange(preset)}>
                  {preset}
                </MenuItem>
              )
            )}
          </Menu>
        </Box>
      </Box>

      {loading ? (
        <Box textAlign="center">
          <CircularProgress size={30} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#e0f7fa", height: 40 }}>
                {[
                  { label: "CREATOR", key: "persion_name" },
                  { label: "CAMPAIGN ID", key: "id" },
                  { label: "USER ID", key: "user_id" },
                  { label: "COUNT", key: "count" },
                  { label: "STATUS", key: "cmp_status" },
                  { label: "DATE&TIME", key: "date" },
                  // { label: "TIME", key: "time" },
                  { label: "REMARKS" },
                  { label: "ACTION" },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    sx={cellStyle}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                    style={{
                      cursor: col.key ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "&:hover": col.key ? { color: "primary.main" } : {},
                      }}
                    >
                      {col.label}
                      {col.key && getSortIcon(col.key)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={cellStyle}>
                    No campaigns found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => {
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          height: 50,
                          backgroundColor: idx % 2 === 0 ? "white" : "#f0f6ff",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                          },
                        }}
                      >
                        <TableCell sx={cellStyle}>
                          {row.persion_name || row.person_name || "N/A"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.id || row.campaign_id || "N/A"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.user_id || row.username || "N/A"}
                        </TableCell>
                        <TableCell sx={cellStyle}>{row.count || "0"}</TableCell>
                        <TableCell sx={cellStyle}>
                          <Chip
                            size="small"
                            label={row.cmp_status || "N/A"}
                            color={
                              row.cmp_status?.toLowerCase() === "completed"
                                ? "success"
                                : row.cmp_status?.toLowerCase() === "inprogress"
                                ? "warning"
                                : "error"
                            }
                          />
                        </TableCell>
                        <TableCell sx={cellStyle}>{row.dateTime}</TableCell>
                        {/* <TableCell sx={cellStyle}>{row.time}</TableCell> */}
                        <TableCell sx={cellStyle}>
                          {row.remarks || "N/A"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {isAdmin && (
                            <Tooltip title="Edit Status">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedCampaign(row);
                                  setEditModalOpen(true);
                                }}
                                sx={{
                                  p: 0.5,
                                  backgroundColor: "black",
                                  color: "white",
                                  mr: 1,
                                  "&:hover": { backgroundColor: "#2196f3" },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download CSV">
                            <IconButton
                              size="small"
                              onClick={() => downloadCSV(row)}
                              sx={{
                                p: 0.5,
                                backgroundColor: "black",
                                color: "white",
                                "&:hover": { backgroundColor: "#43a047" },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableContainer>
      )}

      <EditStatusModal
        open={editModalOpen}
        handleClose={() => setEditModalOpen(false)}
        campaign={selectedCampaign}
        onUpdateSuccess={() => {
          fetchCampaignData();
          setSnackbar({
            open: true,
            message: "Campaign status updated successfully!",
            severity: "success",
          });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignTable;
